import { firstWord, truncateCnpjName, truncateProcessName } from '../../utils/truncate-entity-name'
import type { SignupModalVariant } from './flag'
import type { SignupModalSegment } from './segment'

export interface CopyContext {
  entityName: string
}

export interface CopySegment {
  text: string
  highlight: boolean
}

const t = (text: string): CopySegment => ({ text, highlight: false })
const h = (text: string): CopySegment => ({ text, highlight: true })

export interface CopyResult {
  titleSegments: CopySegment[]
  subtitleSegments: CopySegment[] | null
}

export type CopyResolver = (ctx: CopyContext) => CopyResult

export const FALLBACK_BY_SEGMENT: Record<SignupModalSegment, string> = {
  person: 'este perfil',
  lawsuit: 'este processo',
  other: '',
  unknown: '',
}

export const COMPANY_FALLBACK = 'esta empresa'

const DEFAULT_AUTH = 'Entre ou crie sua conta'
const DEFAULT_AUTH_SENTENCE = `${DEFAULT_AUTH}.`

const CONTROL_COPY: CopyResolver = () => ({
  titleSegments: [t(DEFAULT_AUTH)],
  subtitleSegments: null,
})

const targeted = (resolver: CopyResolver): Record<SignupModalSegment, CopyResolver> => ({
  person: resolver,
  lawsuit: resolver,
  other: CONTROL_COPY,
  unknown: CONTROL_COPY,
})

export const SIGNUP_MODAL_COPY: Record<
  SignupModalVariant,
  Record<SignupModalSegment, CopyResolver>
> = {
  control: targeted(CONTROL_COPY),
  a_cta_echo: targeted(({ entityName }) => ({
    titleSegments: [t(DEFAULT_AUTH)],
    subtitleSegments: [t('Para verificar os processos de '), h(entityName)],
  })),
  b_continuation_in_title: targeted(({ entityName }) => ({
    titleSegments: [t('Continue para ver os processos de '), h(entityName)],
    subtitleSegments: [t(DEFAULT_AUTH_SENTENCE)],
  })),
  c_curiosity_gap: targeted(({ entityName }) => ({
    titleSegments: [h(entityName), t(' tem processos?')],
    subtitleSegments: [t(`${DEFAULT_AUTH} para verificar.`)],
  })),
  d_direct_value: targeted(({ entityName }) => ({
    titleSegments: [t('Veja os processos de '), h(entityName)],
    subtitleSegments: [t(DEFAULT_AUTH_SENTENCE)],
  })),
  e_platform_benefits: targeted(() => ({
    titleSegments: [t(DEFAULT_AUTH)],
    subtitleSegments: [
      t('Entenda processos em linguagem simples, acompanhe atualizações e tire dúvidas.'),
    ],
  })),
}

export type FallbackSentinel = 'no_entity_name' | 'truncate_failed' | null

interface NameResolution {
  displayName: string
  sentinel: FallbackSentinel
}

function resolveEntityName(
  entityName: string | undefined,
  entityType: 'PF' | 'CNPJ' | undefined,
  segment: SignupModalSegment
): NameResolution {
  const fallback = entityType === 'CNPJ' ? COMPANY_FALLBACK : FALLBACK_BY_SEGMENT[segment]
  if (!entityName) {
    return { displayName: fallback, sentinel: 'no_entity_name' }
  }
  let truncated: string
  if (segment === 'lawsuit') {
    truncated = truncateProcessName(entityName)
  } else if (segment === 'person' && entityType === 'CNPJ') {
    truncated = truncateCnpjName(entityName)
  } else {
    truncated = firstWord(entityName)
  }
  if (!truncated || truncated.length === 0) {
    return { displayName: fallback, sentinel: 'truncate_failed' }
  }
  return { displayName: truncated, sentinel: null }
}

const joinSegments = (segments: CopySegment[]): string => segments.map((s) => s.text).join('')

export function contractDemonstrative(text: string): string {
  return text.replace(/\bde (este|esta|esse|essa|aquele|aquela)\b/g, (_match, dem) => `d${dem}`)
}

const capitalizeFirst = (text: string): string =>
  text.length === 0 ? text : text[0].toUpperCase() + text.slice(1)

export interface ResolveSignupModalCopyArgs {
  variant: SignupModalVariant
  segment: SignupModalSegment
  entityName?: string
  entityType?: 'PF' | 'CNPJ'
}

export interface ResolvedSignupModalCopy {
  titleSegments: CopySegment[]
  subtitleSegments: CopySegment[] | null
  title: string
  subtitle: string | null
  sentinel: FallbackSentinel
}

export function resolveSignupModalCopy({
  variant,
  segment,
  entityName,
  entityType,
}: ResolveSignupModalCopyArgs): ResolvedSignupModalCopy {
  const { displayName, sentinel } = resolveEntityName(entityName, entityType, segment)
  const raw = SIGNUP_MODAL_COPY[variant][segment]({ entityName: displayName })

  let titleSegments: CopySegment[]
  let subtitleSegments: CopySegment[] | null

  if (sentinel !== null) {
    titleSegments = [t(capitalizeFirst(contractDemonstrative(joinSegments(raw.titleSegments))))]
    subtitleSegments = raw.subtitleSegments
      ? [t(contractDemonstrative(joinSegments(raw.subtitleSegments)))]
      : null
  } else {
    titleSegments = raw.titleSegments
    subtitleSegments = raw.subtitleSegments
  }

  return {
    titleSegments,
    subtitleSegments,
    title: joinSegments(titleSegments),
    subtitle: subtitleSegments ? joinSegments(subtitleSegments) : null,
    sentinel,
  }
}
